import React from 'react';
import {
  CalendarOff,
  HeartPulse,
  Moon,
  Palmtree,
  Thermometer,
  Timer,
  Umbrella,
} from 'lucide-react';
import { localDateKeyFromDate } from '../../utils/activityDateTime';
import { ActivityIcon } from '../../utils/activityIcons';
import { addDays } from './plantafelViewport';
import type { AbsenceBlock, AbsenceKind, GridBlock } from './plantafelTypes';
import type { EmployeeTimeEntryType } from '../employees/employeeTimeEntryConstants';

export type PlantafelAbsenceDraft = {
  employeeId: string;
  dateFrom: string;
  dateTo: string;
  entryType: EmployeeTimeEntryType;
  startTime: string;
  endTime: string;
  breakMinutes: string;
  note: string;
  entryId?: string | null;
  readOnly?: boolean;
};

/** In der Plantafel erfassbare Abwesenheiten (→ Zeiterfassung). */
export const PLANTAFEL_ABSENCE_ENTRY_TYPES: ReadonlyArray<{
  entryType: EmployeeTimeEntryType;
  kind: AbsenceKind;
  label: string;
}> = [
  { entryType: 'vacation', kind: 'urlaub', label: 'Urlaub' },
  { entryType: 'sickness', kind: 'krank', label: 'Krank (AU)' },
  { entryType: 'shift_free', kind: 'freischicht', label: 'Freischicht' },
  { entryType: 'overtime_comp', kind: 'stundenabbau', label: 'Freizeitausgleich' },
  { entryType: 'unpaid_absence', kind: 'fehlzeit', label: 'Unbezahlte Fehlzeit' },
  { entryType: 'special_leave', kind: 'sonderurlaub', label: 'Sonderurlaub' },
  { entryType: 'sickness_benefit', kind: 'krankengeld', label: 'Krankengeld' },
];

const ABSENCE_TYPE_ICONS: Record<string, React.ReactNode> = {
  vacation: <Palmtree className="h-4 w-4 text-violet-600" />,
  sickness: <Thermometer className="h-4 w-4 text-rose-600" />,
  shift_free: <Moon className="h-4 w-4 text-app-text-subtle" />,
  overtime_comp: <Timer className="h-4 w-4 text-app-text-muted" />,
  unpaid_absence: <CalendarOff className="h-4 w-4 text-app-text-subtle" />,
  special_leave: <Umbrella className="h-4 w-4 text-sky-600" />,
  sickness_benefit: <HeartPulse className="h-4 w-4 text-pink-600" />,
};

export function isPlantafelAbsenceEntryType(entryType: string): boolean {
  return PLANTAFEL_ABSENCE_ENTRY_TYPES.some((o) => o.entryType === entryType);
}

export function plantafelAbsenceLabel(entryType: string): string | null {
  return PLANTAFEL_ABSENCE_ENTRY_TYPES.find((o) => o.entryType === entryType)?.label ?? null;
}

export function plantafelTypeSelectOptions(
  activityTypes: ReadonlyArray<{ id: string; label: string; icon?: string }>,
): Array<{ value: string; label: string; group?: string; icon?: React.ReactNode }> {
  return [
    ...activityTypes
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }))
      .map((t) => ({
        value: t.id,
        label: t.label,
        group: 'Aktivitätstypen',
        icon: <ActivityIcon iconId={t.icon || t.id} className="h-4 w-4 text-pipedrive-blue" />,
      })),
    ...PLANTAFEL_ABSENCE_ENTRY_TYPES.map((o) => ({
      value: o.entryType,
      label: o.label,
      group: 'Abwesenheiten',
      icon: ABSENCE_TYPE_ICONS[o.entryType],
    })),
  ];
}

export function entryTypeToAbsenceKind(entryType: string): AbsenceKind | null {
  return PLANTAFEL_ABSENCE_ENTRY_TYPES.find((o) => o.entryType === entryType)?.kind ?? null;
}

export function absenceKindToEntryType(kind: AbsenceKind): EmployeeTimeEntryType | null {
  if (kind === 'feiertag') return 'public_holiday';
  return PLANTAFEL_ABSENCE_ENTRY_TYPES.find((o) => o.kind === kind)?.entryType ?? null;
}

export function parseAbsenceEntryId(blockId: string): string | null {
  if (!blockId.startsWith('abs-') || blockId.startsWith('abs-merge-')) return null;
  const raw = blockId.slice(4);
  if (!raw || raw.startsWith('holiday-')) return null;
  return raw;
}

export function isPlantafelEditableAbsenceId(id: string): boolean {
  return parseAbsenceEntryId(id) !== null;
}

function toHm(time: string | null | undefined): string {
  if (!time) return '';
  return String(time).slice(0, 5);
}

/** Absence rows that belong to a grid block (single day or merged range). */
function absencesForGridBlock(
  block: GridBlock,
  viewportStart: Date,
  absences: AbsenceBlock[],
): AbsenceBlock[] {
  if (!block.isAbsence || !block.primaryEmployeeId) return [];
  const dateFrom = localDateKeyFromDate(addDays(viewportStart, block.dayStart));
  const dateTo = localDateKeyFromDate(addDays(viewportStart, block.dayEnd));
  return absences
    .filter(
      (a) =>
        a.employee_id === block.primaryEmployeeId &&
        a.absence_kind === block.isAbsence &&
        a.entry_date >= dateFrom &&
        a.entry_date <= dateTo,
    )
    .sort((a, b) => a.entry_date.localeCompare(b.entry_date));
}

export function blockToAbsenceDraft(
  block: GridBlock,
  viewportStart: Date,
  absences: AbsenceBlock[],
): PlantafelAbsenceDraft | null {
  if (!block.isAbsence || !block.primaryEmployeeId) return null;

  const dateFrom = localDateKeyFromDate(addDays(viewportStart, block.dayStart));
  const dateTo = localDateKeyFromDate(addDays(viewportStart, block.dayEnd));
  const entryId = parseAbsenceEntryId(block.id);
  const inRange = absencesForGridBlock(block, viewportStart, absences);
  const absence =
    (entryId ? absences.find((a) => a.id === entryId) : null) ??
    inRange.find((a) => a.start_time && a.end_time) ??
    inRange[0] ??
    null;
  const readOnly = block.isAbsence === 'feiertag' || block.id.includes('holiday-');

  const entryType = (absence?.entry_type ??
    absenceKindToEntryType(block.isAbsence) ??
    'vacation') as EmployeeTimeEntryType;

  const draftEntryId = entryId ?? (inRange.length === 1 ? inRange[0]?.id ?? null : null);

  return {
    employeeId: block.primaryEmployeeId,
    dateFrom,
    dateTo,
    entryType,
    startTime: toHm(absence?.start_time),
    endTime: toHm(absence?.end_time),
    breakMinutes: String(absence?.break_minutes ?? 0),
    note: '',
    entryId: draftEntryId,
    readOnly,
  };
}
